import businessPlanFeatureImage from '../../assets/images/business-plan-feature.png';
import { AiStarsFilledIcon } from '../../assets/icons';
import {
  BUSINESS_PLAN_SECTION,
  SURVEY_ITEM_FINANCIAL_ANALYSIS,
  SURVEY_ITEM_REVIEW,
  SURVEY_ITEM_VARIABLES,
} from '../types';
import useAnalyticsPage from '../../web-analytics/hooks/useAnalyticsPage';
import { Page } from '../../web-analytics/Page';
import { Button } from 'react-bootstrap';
import React from 'react';
import clsx from 'clsx';

export const GetStartedButton = (props: { className?: string; onClick: () => void }) => {
  const { className, onClick } = props;
  return (
    <Button
      type="button"
      className={clsx('button-apply survey-intro__get-started-button', className)}
      disabled={false}
      onClick={onClick}
    >
      Get Started
    </Button>
  );
};

const NavigableText = (props: { text: string; onNavigate: () => void }) => {
  const { text, onNavigate } = props;
  return (
    /* eslint-disable-next-line jsx-a11y/anchor-is-valid */
    <a
      href="#"
      onClick={(e) => {
        e.preventDefault();
        onNavigate();
      }}
      className="growegy-font16--weight700"
      style={{ textDecoration: 'underline' }}
    >
      {text}
    </a>
  );
};

export const BusinessPlanIntro = (props: { onNavigate: (id: string) => void }) => {
  const { onNavigate } = props;
  useAnalyticsPage(Page.BUSINESS_PLAN_INTRO);
  const getOnNavigate = (id: string) => () => onNavigate(id);
  return (
    <div className="survey-info__container">
      <div className="survey-info__block">
        <img src={businessPlanFeatureImage} alt="Why create a business plan" />
        <div>
          <div className="survey-info__block-header">Why Create a Business Plan?</div>
          <ul className="survey-info__list--stars">
            <li>
              <span className="growegy-font16--weight700">To apply</span> for small business loans
              or attracting investors.
            </li>
            <li>
              <span className="growegy-font16--weight700">To expand</span> your business.
            </li>
            <li>
              <span className="growegy-font16--weight700">To align</span> your team around shared
              goals.
            </li>
            <li>
              <span className="growegy-font16--weight700">To identify</span> challenges and
              solutions early&nbsp;on.
            </li>
          </ul>
          <GetStartedButton onClick={getOnNavigate(SURVEY_ITEM_VARIABLES)} />
        </div>
      </div>
      <div className="survey-info__block">
        <div>
          <div className="survey-info__block-header">
            Build the perfect business plan with Growegy
          </div>
          <ol className="survey-info__list--ticks">
            <li>
              <span className="growegy-font16--weight700">Introduction section.</span> You are
              currently reading the intro which offers a high-level overview of the entire business
              plan journey.
            </li>
            <li>
              <NavigableText
                text="Personalization section."
                onNavigate={getOnNavigate(SURVEY_ITEM_VARIABLES)}
              />{' '}
              Provide essential details about your business (i.e. products offered, target
              customers) which enables our AI to help you complete later sections.
            </li>
            <li>
              <NavigableText
                text="Complete each business plan area."
                onNavigate={getOnNavigate(BUSINESS_PLAN_SECTION)}
              />{' '}
              Write the content yourself or use{' '}
              <span className="text-nowrap growegy-font16--weight700">
                <AiStarsFilledIcon className="survey-intro__inline-ai-stars" /> Growegy AI
              </span>{' '}
              to generate tailored suggestions to save you time.
            </li>
            <li>
              <NavigableText
                text="Review your full plan."
                onNavigate={getOnNavigate(SURVEY_ITEM_REVIEW)}
              />{' '}
              <ul>
                <li>View the entire business plan on the Review page.</li>
                <li>
                  Make final adjustments and ensure it reflects your business’s unique identity.
                </li>
              </ul>
            </li>
            <li>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <NavigableText
                text="Download your plan and finalize."
                onNavigate={getOnNavigate(SURVEY_ITEM_REVIEW)}
              />{' '}
              Get your plan in Word format and polish it up by adding relevant images, applying
              styling, and making it truly your own.
            </li>
            <li>
              <NavigableText
                text="Tackle Financial Analysis."
                onNavigate={getOnNavigate(SURVEY_ITEM_FINANCIAL_ANALYSIS)}
              />{' '}
              Choose between:
              <ul>
                <li>
                  Downloading a spreadsheet template to calculate key financial figures on your own
                  and manually adding them to your plan.
                </li>
                <li>
                  Scheduling a consultation with the Growegy team to receive professional assistance
                  in crafting this critical section.
                </li>
              </ul>
            </li>
          </ol>
          <div className="d-flex flex-column m-3">
            <GetStartedButton
              className="align-self-center"
              onClick={getOnNavigate(SURVEY_ITEM_VARIABLES)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
